'use client';

import { createContext, useContext, useState, ReactNode } from 'react';
import { usePopoverModifier } from '@/creditflowresearch/ui/common/components/Explanations/RestrictedAccessExplanation/usePopoverModifier';
import { IPopoverProps } from '@creditflowresearch/uilib/Popover';

interface IDealTooltipContext {
  dealTooltipOpened: string | null,
  setDealTooltipOpened: (state: string | null) => void,
  onClick: (e: React.MouseEvent, tooltipId: string) => void,
  popoverOffsetOptions: IPopoverProps['offsetOptions'],
}

const useDealTooltip = () => {
  const [dealTooltipOpened, setDealTooltipOpened] = useState<string | null>(null);
  const { popoverOffsetOptions, setTooltipOffset, getTooltipOffset } = usePopoverModifier();

  const onClick = (e: React.MouseEvent, tooltipId: string) => {
    e.stopPropagation();
    e.preventDefault();

    const newOffset = getTooltipOffset(e);
    /**
     * We need to close previous tooltip before opening a new one.
     * Otherwise, the new tooltip will not be positioned correctly.
     */
    setDealTooltipOpened(null);
    setTimeout(() => {
      setTooltipOffset(newOffset);
      setDealTooltipOpened(tooltipId);
    }, 0);
  };

  return {
    dealTooltipOpened,
    setDealTooltipOpened,
    onClick,
    popoverOffsetOptions,
  };
};

const DealTooltipContext = createContext<IDealTooltipContext | undefined>(undefined);

export const DealTooltipProvider = ({ children }: { children: ReactNode }) => {
  return (
    <DealTooltipContext.Provider value={ useDealTooltip() }>
      { children }
    </DealTooltipContext.Provider>
  );
};

export const useDealTooltipContext = () => {
  const context = useContext(DealTooltipContext);
  if (!context) {
    throw new Error('useDealTooltipContext must be used within a DealTooltipProvider');
  }
  return context;
};
